import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, Package, Tag, DollarSign, Clock, FileText, AlertCircle, RefreshCw } from 'lucide-react';
import Navigation from '../../components/Navigation';
import { offerService } from '../../services/offerService';
import { supabase } from '../../lib/supabase';
import SellerInfo from './components/SellerInfo';
import ActionButtons from './components/ActionButtons';

const TYPE_CONFIG = {
  kupovina: { label: 'Kupovina', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  prodaja: { label: 'Prodaja', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  razmena: { label: 'Razmena', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
};

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr)?.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatPrice(price, currency) {
  if (price === null || price === undefined) return 'Po dogovoru';
  return `${Number(price)?.toLocaleString('sr-RS')} ${currency || 'RSD'}`;
}

export default function OfferDetailsDetaljiOglasa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [offer, setOffer] = useState(location?.state?.offer || null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(!location?.state?.offer);
  const [error, setError] = useState('');
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  useEffect(() => {
    if (!offer && id) {
      fetchOffer();
    } else if (offer) {
      fetchSeller(offer?.userId);
    }
  }, [id]);

  const fetchOffer = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await offerService?.getOfferById(id);
      setOffer(data);
      if (data?.userId) fetchSeller(data?.userId);
    } catch (err) {
      setError(err?.message || 'Greška pri učitavanju oglasa');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeller = async (userId) => {
    if (!userId) return;
    try {
      const { data } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, rating, profile_picture_url')
        ?.eq('id', userId)
        ?.single();
      setSeller(data);
    } catch (err) {
      // Seller info not critical
    }
  };

  const handleAccept = async (offerData) => {
    setAcceptSuccess(true);
    setTimeout(() => setAcceptSuccess(false), 4000);
  };

  const typeConf = TYPE_CONFIG?.[offer?.wishType?.toLowerCase()] || TYPE_CONFIG?.prodaja;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-72 bg-gray-200 rounded-xl" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <AlertCircle size={40} className="text-red-400 mx-auto mb-3" />
            <p className="text-red-600 font-medium mb-4">{error}</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-sm">
                Nazad
              </button>
              <button onClick={fetchOffer} className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 text-sm">
                <RefreshCw size={14} /> Pokušaj ponovo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Oglas nije pronađen</p>
          <button onClick={() => navigate('/oglasna-tabla-board')} className="mt-4 px-4 py-2 bg-teal-700 text-white rounded-lg text-sm">
            Nazad na oglasnu tablu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Nazad na oglasnu tablu</span>
        </button>

        {/* Accept success banner */}
        {acceptSuccess && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm font-medium">
            ✅ Ponuda je prihvaćena! Prodavac će biti obavešten.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Info + Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Katalog info */}
            <div className="bg-teal-50 rounded-xl border border-teal-200 p-5 text-sm text-teal-800">
              Detalje token-vaučera možete filtrirati i pregledati na katalogu{' '}
              <span className="font-bold">
                <a
                  href="https://valuebook.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-teal-600"
                >
                  valuebook.app
                </a>
              </span>
            </div>

            {/* Main Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{offer?.voucherName || 'Nepoznat vaučer'}</h1>
                  {offer?.voucherSymbol && (
                    <div className="flex items-center gap-1 mt-1 text-gray-500">
                      <Tag size={14} />
                      <span className="text-sm">{offer?.voucherSymbol}</span>
                    </div>
                  )}
                </div>
                <span className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold border ${typeConf?.bg} ${typeConf?.text} ${typeConf?.border}`}>
                  {typeConf?.label}
                </span>
              </div>

              {/* Key details grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Package size={13} /> Količina
                  </div>
                  <p className="font-semibold text-gray-900">
                    {offer?.tokenQuantity ? offer?.tokenQuantity?.toLocaleString('sr-RS') : 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <DollarSign size={13} /> Cena
                  </div>
                  <p className="font-semibold text-teal-700">{formatPrice(offer?.maxPrice, offer?.currency)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <Calendar size={13} /> Datum objave
                  </div>
                  <p className="font-semibold text-gray-900">{formatDate(offer?.createdAt)}</p>
                </div>
                {offer?.expiryDate && (
                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-orange-500 text-xs mb-1">
                      <Clock size={13} /> Ističe
                    </div>
                    <p className="font-semibold text-orange-600">{formatDate(offer?.expiryDate)}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {offer?.description && (
                <div className="mb-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <FileText size={15} /> Opis
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">{offer?.description}</p>
                </div>
              )}

              {/* Special conditions */}
              {offer?.specialConditions && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-amber-800 mb-1">⚠️ Posebni uslovi</h3>
                  <p className="text-amber-700 text-sm whitespace-pre-wrap">{offer?.specialConditions}</p>
                </div>
              )}

              {/* Exchange wishes */}
              {offer?.exchangeWishes && offer?.wishType?.toLowerCase() === 'razmena' && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-purple-800 mb-1">🔄 Željeni vaučeri u zamenu</h3>
                  <p className="text-purple-700 text-sm">{offer?.exchangeWishes}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="pt-4 border-t border-gray-100">
                <ActionButtons offer={offer} onAccept={handleAccept} />
              </div>
            </div>
          </div>

          {/* Right column - Seller + Summary */}
          <div className="space-y-4">
            {/* Seller info */}
            <SellerInfo seller={seller} />

            {/* Offer summary card */}
            <div className="bg-teal-50 rounded-xl border border-teal-200 p-4">
              <h3 className="text-sm font-semibold text-teal-800 mb-3">Rezime ponude</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-teal-600">Tip:</span>
                  <span className="font-medium text-teal-900">{typeConf?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-teal-600">Količina:</span>
                  <span className="font-medium text-teal-900">{offer?.tokenQuantity?.toLocaleString('sr-RS') || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-teal-600">Cena:</span>
                  <span className="font-medium text-teal-900">{formatPrice(offer?.maxPrice, offer?.currency)}</span>
                </div>
                {offer?.currency && (
                  <div className="flex justify-between">
                    <span className="text-teal-600">Valuta:</span>
                    <span className="font-medium text-teal-900">{offer?.currency}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
